/**
 * Billing maintenance (TMMPDM-0094, tmm-api 0161): one-off billing set-up actions on Product settings.
 * Today one: Backfill payees… (`POST /v1/admin/billing/payees/backfill`), which registers the existing
 * partners as pay-api payees. The Operator runs it once, after pay-api's adoption. The confirm is
 * non-destructive (the action is audited and idempotent). The answer's counts are shown as sent,
 * labelled from the contract, in a status line that stays until the next run replaces it; a failure
 * is said inside the confirm, which stays open (TMM-B4).
 * TMMPDM-0100: ledger cut-over is its own Product settings item.
 */
import { useState } from 'react';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { ConfirmDialog, SectionCard } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { errorCopy } from '@/api/errors';
import type { PayeeBackfillResult } from '@/api/tmm/types';

export function BillingMaintenanceSection() {
  const { t } = useCopy();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The last run's answer; it stays until the next run replaces it.
  const [result, setResult] = useState<PayeeBackfillResult | null>(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      setResult(await adminApi.backfillPayees());
      setOpen(false);
    } catch (err: unknown) {
      setError(
        errorCopy(
          err,
          t,
          t('settings.maintenance.backfill.error', "Couldn't run the payee backfill."),
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  /** The contract's counts as sent, in its order. `failed` is a list of ids, shown by its length. */
  const counts = (answer: PayeeBackfillResult): string[] => [
    t('settings.maintenance.backfill.matched', 'Matched {{value, number}}', {
      value: answer.matched,
    }),
    t('settings.maintenance.backfill.stored', 'Stored {{value, number}}', {
      value: answer.stored,
    }),
    t('settings.maintenance.backfill.kindFixed', 'Kind corrected {{value, number}}', {
      value: answer.kindFixed,
    }),
    t(
      'settings.maintenance.backfill.kindUnavailable',
      'Kind not correctable yet {{value, number}}',
      { value: answer.kindUnavailable },
    ),
    t('settings.maintenance.backfill.policySet', 'Policy set {{value, number}}', {
      value: answer.policySet,
    }),
    t('settings.maintenance.backfill.unmatched', 'Unmatched {{value, number}}', {
      value: answer.unmatched,
    }),
    t('settings.maintenance.backfill.failed', 'Failed {{value, number}}', {
      value: answer.failed.length,
    }),
  ];

  return (
    <SectionCard
      title={t('settings.maintenance.title', 'Billing maintenance')}
      subtitle={t('settings.maintenance.help', 'One-off actions for the billing setup.')}
      id="settings-maintenance-title"
    >
      <Stack spacing={1} sx={{ alignItems: 'flex-start', maxWidth: 640 }}>
        <Button
          variant="outlined"
          onClick={() => {
            setError(null);
            setOpen(true);
          }}
        >
          {t('settings.maintenance.backfill.action', 'Backfill payees…')}
        </Button>
        {result ? (
          <Alert severity={result.failed.length > 0 ? 'warning' : 'success'} role="status">
            <Typography variant="body2">
              {t('settings.maintenance.backfill.done', 'Payee backfill finished.')}{' '}
              {counts(result).join(' · ')}
            </Typography>
            {result.failed.length > 0 ? (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {t('settings.maintenance.backfill.failedIds', 'Not stored: {{ids}}', {
                  ids: result.failed.join(', '),
                })}
              </Typography>
            ) : null}
            {result.kindUnavailable > 0 ? (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {t(
                  'settings.maintenance.backfill.runAgain',
                  "Some payees' kind couldn't be corrected yet. Run this again once pay-api has it.",
                )}
              </Typography>
            ) : null}
          </Alert>
        ) : null}
      </Stack>
      <ConfirmDialog
        open={open}
        title={t(
          'settings.maintenance.backfill.title',
          'Register existing partners as payees in pay-api?',
        )}
        consequence={t(
          'settings.maintenance.backfill.body',
          "Run this once, after pay-api's adoption. It's audited.",
        )}
        confirmLabel={t('settings.maintenance.backfill.confirm', 'Backfill payees')}
        busy={busy}
        error={error}
        onConfirm={() => void run()}
        onCancel={() => setOpen(false)}
      />
    </SectionCard>
  );
}
